import { Avatar, BottomSheet, Button, Typography } from '@/ui-kit';
import styles from './avatar-profile.module.scss';
import { ErrorNotificationIcon, FillProfileIcon, OkHandIcon, VerificationIcon, VerifiedAccountIcon } from '@/assets/icons';
import { routesApp } from '@/constans/routes';
import { profilesApi } from '@/api';
import { useAuth } from '@/store/useAuth';
import { useEffect, useState } from 'react';
import debounce from 'lodash/debounce';
import { callYaMetrika } from '@/utils/call-ya-metrika';
import { useToast } from '@/store/useToast';
import { useNavigate } from 'react-router-dom';
import WebApp from '@twa-dev/sdk';
import clsx from 'clsx';
import { createPortal } from 'react-dom';

interface Props {
    avatar?: string;
    name?: string;
    // email?: string;
}

export const AvatarProfile: React.FC<Props> = ({ avatar, name }) => {
    const userData = useAuth((state) => state.getUser());
    const setUserData = useAuth((state) => state.setProfile);
    
    const showToast = useToast((state) => state.show);

    const [isActivated, setIsActivated] = useState<boolean | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false);

    useEffect(() => {
        setIsActivated(userData?.active ?? false);
    }, [userData]);

    const deactivateProfileDebounce = debounce(async () => {
        if (isLoading) return;
        setIsLoading(true);
        callYaMetrika('deactivate_account');

        try {
            const res = await profilesApi.deactivate();
            if (res.status !== 200) return showDeactivateError();
            showToast({
                // type success
                icon:
                    <svg
                        width="20"
                        height="19.61"
                        viewBox="0 0 28 28"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M1.59323 0.273438L0.273438 1.59323L26.4068 27.7266L27.7266 26.4068L22.1029 20.7831C25.9256 18.1947 27.8126 14.5755 27.8378 14.5268C27.9436 14.3716 28.0002 14.1879 28 14C27.9999 13.7895 27.9286 13.5852 27.7977 13.4203C27.7004 13.2638 22.3237 4.66667 14 4.66667C11.6385 4.66667 9.51938 5.36685 7.68542 6.36562L1.59323 0.273438ZM14 7.46667C17.6083 7.46667 20.5333 10.3917 20.5333 14C20.5333 15.4672 20.0387 16.812 19.2208 17.901L16.5211 15.2013C16.697 14.838 16.8 14.432 16.8 14C16.8 12.4535 15.5465 11.2 14 11.2C13.568 11.2 13.162 11.303 12.7987 11.4789L10.099 8.77917C11.188 7.96129 12.5328 7.46667 14 7.46667ZM4.62109 8.47109C1.84721 10.8044 0.256649 13.3346 0.204167 13.4185C0.0722298 13.5836 0.000247743 13.7886 0 14C0.000165576 14.1806 0.052724 14.3573 0.151302 14.5086C0.152511 14.5104 0.153726 14.5122 0.154948 14.5141C0.170995 14.5472 4.68424 23.3333 14 23.3333C15.7267 23.3333 17.2858 23.0286 18.6849 22.5367L16.2695 20.1195C15.5611 20.3827 14.7989 20.5333 14 20.5333C10.3917 20.5333 7.46667 17.6083 7.46667 14C7.46667 13.2011 7.61727 12.4389 7.88047 11.7305L4.62109 8.47109Z"
                            fill="#fff" />
                    </svg>,
                msg: 'Ваш профиль скрыт'
            });
            setIsActivated(false);
            setUserData({
                ...userData,
                active: false,
            });
        } catch (e) {
            showDeactivateError();
        } finally {
            setIsLoading(false);
        }
    }, 750);

    const navigate = useNavigate();

    const showDeactivateError = () =>
        showToast({
            icon: <ErrorNotificationIcon />,
            msg: 'Ошибка. Попробуйте ещё раз'
        });

    const onMyFormPreviewClick = () => {
        callYaMetrika('my_form_preview');
        navigate(routesApp.myFormPreview)
    };

    return (
        <div className={styles.wrapper}>
            <div
                className={
                    clsx(
                        styles.avatarWrapper,
                        userData?.is_paid && styles['avatarWrapper--online']
                    )
                }
                onClick={onMyFormPreviewClick}>
                <Avatar
                    src={avatar}
                    size={120} />
                {/* <span className={styles.status}></span> */}
            </div>
            <div className={styles.nameAndVerificationStatusInfo}>
                <Typography
                    className={styles.name}
                    typeColor="black"
                    center>
                    {name}
                </Typography>
                {
                    userData
                        ?.verification_status
                        === 'verified' &&
                    <VerifiedAccountIcon
                        width={24}
                        height={24}
                        className={styles.verificationStatusInfo}
                        onClick={() => setIsVerificationModalOpen(true)} />
                }
            </div>

            <Button
                className={styles.button}
                textColor="black"
                to={routesApp.profileEdit}
                isLink>
                <FillProfileIcon />
                <span>Заполнить профиль</span>
            </Button>

            <BottomSheet
                overlayClassName={styles.bottomsheetOverlay}
                isOpen={isVerificationModalOpen}
                isDeskorTop={false}
                onClose={() => setIsVerificationModalOpen(false)}>
                <div className={styles.verificationModalContent}>
                    <div className={styles.verificationModalContentIcon}>
                        <Avatar
                            src={avatar}
                            size={96} />
                        <VerifiedAccountIcon
                            className={styles.verificationModalContentIconVerification}
                            width={28}
                            height={28} />
                    </div>
                    <div className={styles.verificationModalContentHeader}>
                        <Typography
                            className={styles.verificationModalContentHeaderTitle}
                            variant={'h5'}
                            fontWeight={'bold'}>
                            Верификация пройдена!
                        </Typography>
                        <Typography
                            className={styles.verificationModalContentHeaderDesc}>
                            Галочка подтверждает, что твоя анкета подтверждена по видео. Это помогает другим пользователям видеть, что за профилем стоит реальный человек.
                        </Typography>
                    </div>
                    <div className={styles.verificationModalContentFootnote}>
                        <Button
                            typeColor="orange"
                            className={styles.verificationModalContentFootnoteBtn}
                            onClick={() => setIsVerificationModalOpen(false)}>
                            Понятно!
                        </Button>
                    </div>
                </div>
            </BottomSheet>

        </div>
    );
};
